"use client";

import * as React from "react";

import useEmblaCarousel, {
  type UseEmblaCarouselType,
} from "embla-carousel-react";

import { cn } from "../util.cn/cn";

type CarouselApi = UseEmblaCarouselType[1];
type UseCarouselParameters = Parameters<typeof useEmblaCarousel>;
type CarouselOptions = UseCarouselParameters[0];
type CarouselPlugin = UseCarouselParameters[1];

type CarouselProps = {
  opts?: CarouselOptions;
  plugins?: CarouselPlugin;
};

type CarouselContextProps = {
  canScrollNext: boolean;
  canScrollPrev: boolean;
  carouselMainApi: ReturnType<typeof useEmblaCarousel>[1];
  carouselMainRef: ReturnType<typeof useEmblaCarousel>[0];
  scrollNext: () => void;
  scrollPrev: () => void;
  scrollTo: (index: number) => void;
  selectedIndex: number;
} & CarouselProps;

const CarouselContext = React.createContext<CarouselContextProps | null>(null);

const useCarousel = () => {
  const context = React.useContext(CarouselContext);

  if (!context) {
    throw new Error("useCarousel must be used within a <ImageCarousel.Provider />");
  }

  return context;
};

const Provider = React.forwardRef<
  HTMLDivElement,
  CarouselProps & React.HTMLAttributes<HTMLDivElement>
>(
  (
    {
      children,
      className,
      opts,
      plugins,
      ...props
    },
    ref,
  ) => {
    // -> init carousel instance
    const [carouselMainRef, carouselMainApi] = useEmblaCarousel(
      {
        ...opts,
      },
      plugins,
    );

    // -> set state
    const [selectedIndex, setSelectedIndex] = React.useState(0);
    const [canScrollPrev, setCanScrollPrev] = React.useState(false);
    const [canScrollNext, setCanScrollNext] = React.useState(false);

    // -> define event
    const onSelect = React.useCallback(() => {
      if (!carouselMainApi) {
        return;
      }
      const idx = carouselMainApi.selectedScrollSnap();

      setSelectedIndex(idx);
      setCanScrollPrev(carouselMainApi.canScrollPrev());
      setCanScrollNext(carouselMainApi.canScrollNext());
    }, [
      carouselMainApi,
      setSelectedIndex,
    ]);

    const scrollPrev = React.useCallback(() => {
      carouselMainApi?.scrollPrev();
    }, [carouselMainApi]);

    const scrollNext = React.useCallback(() => {
      carouselMainApi?.scrollNext();
    }, [carouselMainApi]);

    const scrollTo = React.useCallback((index: number) => {
      carouselMainApi?.scrollTo(index);
    }, [carouselMainApi]);

    React.useEffect(() => {
      if (!carouselMainApi) {
        return;
      }

      onSelect();

      carouselMainApi.on("select", onSelect).on("reInit", onSelect);
    }, [carouselMainApi, onSelect]);

    return (
      <CarouselContext.Provider
        value={{
          canScrollNext,
          canScrollPrev,
          carouselMainApi,
          carouselMainRef,
          opts,
          scrollNext,
          scrollPrev,
          scrollTo,
          selectedIndex,
        }}
      >
        <div
          aria-roledescription="carousel"
          className={cn("relative", className)}
          ref={ref}
          role="region"
          {...props}
        >
          {children}
        </div>
      </CarouselContext.Provider>
    );
  },
);
Provider.displayName = "CarouselProvider";

interface ContentProps extends React.HTMLAttributes<HTMLDivElement> {
  overflowClassName?: string;
}
const Content = React.forwardRef<
  HTMLDivElement,
  ContentProps
>(({ className, overflowClassName, ...props }, ref) => {
  const { carouselMainRef } = useCarousel();

  return (
    <div
      className={
        cn(
          "overflow-hidden",
          overflowClassName,
        )
      }
      ref={carouselMainRef}
    >
      <div
        className={cn(
          "flex",
          className,
        )}
        ref={ref}
        {...props}
      />
    </div>
  );
});
Content.displayName = "CarouselContent";

interface ItemProps extends React.HTMLAttributes<HTMLDivElement> {}
const Item = React.forwardRef<
  HTMLDivElement,
  ItemProps
>((props, ref) => {
  const { className, children, ...divProps } = props;
  return (
    <div
      aria-roledescription="slide"
      className={cn(
        `relative w-full shrink-0 grow-0 basis-full`,
        className,
      )}
      ref={ref}
      role="group"
      {...divProps}
    >
      {children}
    </div>
  );
});
Item.displayName = "CarouselItem";

interface ItemImageProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "children"> {
  imgSrc: string;
}
const ItemImage = React.forwardRef<
  HTMLDivElement,
  ItemImageProps
>((props, ref) => {
  const { className, imgSrc, ...divProps } = props;
  return (
    <div
      ref={ref}
      className={cn(
        "absolute inset-0 overflow-hidden",
        className,
      )}
      {...divProps}
    >
      {/* eslint-disable-next-line jsx-a11y/alt-text */}
      <img className="size-full object-cover object-center" src={imgSrc} />
    </div>
  );
});
ItemImage.displayName = "CarouselItemImage";

interface ItemContentProps extends React.HTMLAttributes<HTMLDivElement> {}
const ItemContent = React.forwardRef<
  HTMLDivElement,
  ItemContentProps
>((props, ref) => {
  const { className, children, ...divProps } = props;
  return (
    <div
      ref={ref}
      className={cn(
      `
        relative py-32
        sm:py-64
      `,
      className,
      )}
      {...divProps}
    >
      {children}
    </div>
  );
});
ItemContent.displayName = "CarouselItemContent";

export {
  type CarouselApi,
  Content,
  Item,
  ItemContent,
  ItemImage,
  Provider,
};
